import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import {
  ArrowRight,
  CalendarDays,
  FileText,
  Hash,
  Home,
  Loader2,
  Phone,
  Save,
  Trash2,
  User,
} from "lucide-react";
import { LazyLoading } from "./ui/LazyLoading";
import ConfirmModal from "./ui/ConfirmModal";
import toast from "react-hot-toast";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { CUSTOMER_LABELS, validateCustomer } from "../utils/recordValidation";
import { formatNumber } from "../utils/numberFormat";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const emptyCustomer = {
  full_name: "",
  phone_number: "",
  customer_birthday: "",
  national_code: "",
  customer_address: "",
};

const Card = ({ title, icon: Icon, children, actions, className = "" }) => (
  <section
    className={cx(
      "rounded-2xl border border-slate-200 bg-white shadow-sm",
      className
    )}
  >
    <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">{actions}</div>
        <div className="flex items-center gap-3 text-right">
          <div>
            <h2 className="text-base font-extrabold text-slate-900 sm:text-lg">
              {title}
            </h2>
          </div>
          {Icon ? (
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Icon size={20} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const Field = ({
  id,
  label,
  icon: Icon,
  value,
  onChange,
  type = "text",
  inputMode,
  children,
}) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-right text-sm font-medium text-slate-700">
      {label}
    </label>
    {children || (
      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={18} />
          </span>
        ) : null}
        <input
          id={id}
          name={id}
          type={type}
          inputMode={inputMode}
          value={value || ""}
          onChange={onChange}
          className={cx(
            "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900 shadow-sm",
            "placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25",
            Icon ? "pr-10" : "pr-3"
          )}
        />
      </div>
    )}
  </div>
);

const TextAreaField = ({ id, label, value, onChange }) => (
  <div className="space-y-1.5 md:col-span-2">
    <label htmlFor={id} className="block text-right text-sm font-medium text-slate-700">
      {label}
    </label>
    <textarea
      id={id}
      name={id}
      value={value || ""}
      onChange={onChange}
      rows={4}
      className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
    />
  </div>
);

const StatusChip = ({ value }) => (
  <span
    className={cx(
      "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium",
      value
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-slate-200 bg-slate-50 text-slate-600"
    )}
  >
    {value ? "شده" : "نشده"}
  </span>
);

const normalizeCottages = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const CustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [cottages, setCottages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const [customerResponse, cottagesResponse] = await Promise.all([
          axiosInstance.get(`/accounts/customers/by-id/${customerId}/`),
          axiosInstance.get(`/cottages/?cottage_customer=${customerId}`),
        ]);

        if (!mounted) return;
        setCustomer({ ...emptyCustomer, ...customerResponse.data });
        setCottages(normalizeCottages(cottagesResponse.data));
      } catch (err) {
        if (!mounted) return;
        setError("خطا در دریافت اطلاعات مشتری");
        toastApiError(err, {}, "دریافت اطلاعات مشتری ناموفق بود.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [customerId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setCustomer((prev) => ({
      ...prev,
      customer_birthday: date ? date.format("YYYY-MM-DD") : "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!customer) return;
    const { lines: problems } = validateCustomer(customer);
    if (problems.length) {
      toastErrors(problems);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const response = await axiosInstance.put(
        `/accounts/customers/${customerId}/`,
        customer
      );
      setCustomer({ ...emptyCustomer, ...response.data });
      toast.success("تغییرات با موفقیت ذخیره شد.");
    } catch (err) {
      toastApiError(err, CUSTOMER_LABELS, "ذخیره تغییرات مشتری ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteOpen(false);
    try {
      await axiosInstance.delete(`/accounts/customers/${customerId}/`);
      toast.success("مشتری با موفقیت حذف شد.");
      navigate("/customers/list");
    } catch (err) {
      toastApiError(err, {}, "حذف مشتری ناموفق بود.");
    }
  };

  if (loading) return <LazyLoading />;

  if (!customer) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 pt-28 font-vazir" dir="rtl">
        <div className="mx-auto max-w-3xl rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-right text-rose-800">
          {error || "اطلاعات مشتری یافت نشد."}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 pb-10 pt-24 font-vazir md:px-6 md:pt-28" dir="rtl">
      <ConfirmModal
        isOpen={deleteOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      <div className="mx-auto max-w-7xl space-y-5">
        <header className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Link
                to="/customers/list"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <ArrowRight size={16} />
                بازگشت
              </Link>
            </div>

            <div className="text-right">
              <div className="flex items-center justify-end gap-3">
                <div>
                  <h1 className="text-2xl font-extrabold text-slate-900 md:text-3xl">
                    {customer.full_name || "مشتری بدون نام"}
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    جزئیات مشتری و اظهارنامه‌های مرتبط
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <User size={24} />
                </div>
              </div>
            </div>
          </div>
        </header>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3 text-right text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <Card
            title="اطلاعات مشتری"
            icon={User}
            className="lg:col-span-2"
            actions={
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 text-sm font-semibold text-rose-700 hover:bg-rose-100"
              >
                <Trash2 size={16} />
                حذف
              </button>
            }
          >
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field
                  id="full_name"
                  label="نام مشتری"
                  icon={User}
                  value={customer.full_name}
                  onChange={handleChange}
                />
                <Field
                  id="phone_number"
                  label="تلفن مشتری"
                  icon={Phone}
                  value={customer.phone_number}
                  onChange={handleChange}
                  inputMode="tel"
                />
                <Field
                  id="national_code"
                  label="کد ملی"
                  icon={Hash}
                  value={customer.national_code}
                  onChange={handleChange}
                  inputMode="numeric"
                />
                <Field id="customer_birthday" label="تاریخ تولد" icon={CalendarDays}>
                  <div className="relative">
                    <span className="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-slate-400">
                      <CalendarDays size={18} />
                    </span>
                    <DatePicker
                      id="customer_birthday"
                      calendar={persian}
                      locale={persian_fa}
                      value={customer.customer_birthday || ""}
                      onChange={handleDateChange}
                      format="YYYY-MM-DD"
                      placeholder="----/--/--"
                      inputClass="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                      containerClassName="w-full"
                    />
                  </div>
                </Field>
                <TextAreaField
                  id="customer_address"
                  label="آدرس مشتری"
                  value={customer.customer_address}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-right text-xs leading-5 text-slate-500">
                  تغییرات روی همین رکورد مشتری ذخیره می‌شود.
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex h-11 min-w-[150px] items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 size={17} className="animate-spin" />
                  ) : (
                    <Save size={17} />
                  )}
                  ذخیره تغییرات
                </button>
              </div>
            </form>
          </Card>

          <Card title="خلاصه" icon={FileText}>
            <div className="space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500">تعداد اظهارنامه‌ها</div>
                <div className="mt-1 text-2xl font-extrabold text-slate-900">
                  {formatNumber(cottages.length)}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500">تلفن</div>
                <div className="mt-1 break-words text-sm font-semibold text-slate-900">
                  {customer.phone_number || "—"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500">کد ملی</div>
                <div className="mt-1 break-words text-sm font-semibold text-slate-900">
                  {customer.national_code || "—"}
                </div>
              </div>
            </div>
          </Card>
        </div>

        <Card title="اظهارنامه‌های مرتبط" icon={Home}>
          {cottages.length > 0 ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr className="text-right">
                      <th className="p-3 font-semibold">شماره کوتاژ</th>
                      <th className="p-3 font-semibold">تاریخ کوتاژ</th>
                      <th className="p-3 font-semibold">ثبت سفارش</th>
                      <th className="p-3 font-semibold">ارزش کل</th>
                      <th className="p-3 font-semibold">ارزش گمرکی</th>
                      <th className="p-3 font-semibold">تعداد</th>
                      <th className="p-3 font-semibold">رفع تعهد</th>
                      <th className="p-3 font-semibold">بازبینی</th>
                      <th className="p-3 font-semibold">خروج شده</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {cottages.map((cottage) => (
                      <tr key={cottage.id} className="hover:bg-slate-50">
                        <td className="p-3 font-medium">
                          <Link
                            to={`/cottages/${cottage.cottage_number}`}
                            className="text-blue-700 hover:underline"
                          >
                            {cottage.cottage_number || "—"}
                          </Link>
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          {cottage.cottage_date || "—"}
                        </td>
                        <td className="p-3">
                          {cottage.is_patteh ? (
                            "پته"
                          ) : cottage.proforma?.prfVCodeInt ? (
                            <Link
                              to={`/order-details/${cottage.proforma.prfVCodeInt}`}
                              className="text-blue-700 hover:underline"
                            >
                              {cottage.proforma?.prf_order_no || "—"}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          {formatNumber(cottage.total_value)}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          {formatNumber(cottage.customs_value)}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          {formatNumber(cottage.quantity)}
                        </td>
                        <td className="p-3">
                          <StatusChip value={cottage.rafee_taahod} />
                        </td>
                        <td className="p-3">
                          <StatusChip value={cottage.rewatch} />
                        </td>
                        <td className="p-3">
                          <StatusChip value={cottage.exited} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
              هیچ کوتاژی برای این مشتری یافت نشد.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CustomerDetails;
